import {
  Building2,
  Users,
  Mail,
  Loader2,
  Plus,
  Trash2,
  ShieldCheck,
  KeyRound,
  Copy,
  Check,
  Pencil,
  Power,
  PowerOff,
  Activity,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { InviteMemberDialog } from '@/components/orgs/InviteMemberDialog.js';
import { SsoConfigDialog } from '@/components/orgs/SsoConfigDialog.js';
import { Slot } from '@/components/slots/Slot.js';
import { api, ApiError, type AuditEntry, type Organisation, type OrgSsoConfig } from '@/api/client.js';
import { cn } from '@/lib/utils.js';

export function OrgDetailPage(): JSX.Element {
  const { slug } = useParams({ strict: false }) as { slug?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [ssoOpen, setSsoOpen] = useState<{ existing: OrgSsoConfig | null } | null>(null);

  const orgsQuery = useQuery({
    queryKey: ['organisations'],
    queryFn: () => api.organisations.list(),
  });

  const org = orgsQuery.data?.find((o) => o.slug === slug);

  const membersQuery = useQuery({
    queryKey: ['org-members', org?.id],
    queryFn: () => api.organisations.members(org!.id),
    enabled: !!org,
  });

  const invitationsQuery = useQuery({
    queryKey: ['org-invitations', org?.id],
    queryFn: () => api.organisations.pendingInvitations(org!.id),
    enabled: !!org,
  });

  const ssoQuery = useQuery({
    queryKey: ['org-sso', org?.id],
    queryFn: () => api.organisations.ssoConfigs(org!.id),
    enabled: !!org,
  });

  const auditQuery = useQuery({
    queryKey: ['org-audit', org?.id],
    queryFn: () => api.organisations.audit(org!.id, { limit: 25 }),
    enabled: !!org,
  });

  if (orgsQuery.isLoading) {
    return (
      <div className="flex justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!org) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm">
          <Building2 className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p>That organisation isn't visible to you, or doesn't exist.</p>
          <Button variant="link" onClick={() => navigate({ to: '/orgs' })}>
            Back to organisations
          </Button>
        </CardContent>
      </Card>
    );
  }

  async function revoke(invitationId: string) {
    try {
      await api.invitations.revoke(invitationId);
      toast.success('Invitation revoked');
      await queryClient.invalidateQueries({ queryKey: ['org-invitations', org!.id] });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not revoke');
    }
  }

  async function deleteSso(id: string, provider: string) {
    if (!confirm(`Delete ${provider} SSO config? Members already signed in keep their sessions.`)) return;
    try {
      await api.organisations.deleteSsoConfig(org!.id, id);
      toast.success('SSO config deleted');
      await queryClient.invalidateQueries({ queryKey: ['org-sso', org!.id] });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete SSO config');
    }
  }

  async function toggleSsoEnabled(cfg: OrgSsoConfig) {
    try {
      await api.organisations.updateSsoConfig(org!.id, cfg.id, { enabled: !cfg.enabled });
      toast.success(`SSO ${cfg.enabled ? 'disabled' : 'enabled'}`);
      await queryClient.invalidateQueries({ queryKey: ['org-sso', org!.id] });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not toggle SSO');
    }
  }

  return (
    <div className="space-y-8">
      <OrgHeader org={org} />

      <MembersCard
        members={membersQuery.data ?? []}
        loading={membersQuery.isLoading}
        onInvite={() => setInviteOpen(true)}
      />

      <InvitationsCard
        invitations={invitationsQuery.data ?? []}
        loading={invitationsQuery.isLoading}
        onRevoke={revoke}
      />

      <SsoCard
        configs={ssoQuery.data ?? []}
        loading={ssoQuery.isLoading}
        onAdd={() => setSsoOpen({ existing: null })}
        onEdit={(cfg) => setSsoOpen({ existing: cfg })}
        onDelete={(cfg) => deleteSso(cfg.id, cfg.provider)}
        onToggleEnabled={toggleSsoEnabled}
      />

      <ActivityCard entries={auditQuery.data ?? []} loading={auditQuery.isLoading} />

      {/*
        Plugin-contributed cards. Today the applications plugin registers an
        "Applications" card here listing apps directly owned by this org;
        more plugins (backup summary, monitoring rollup, …) will register
        against the same slot as they land. Adding a new fragment is a
        one-line append in `components/slots/registry.ts` — this page
        doesn't change.
      */}
      <Slot
        name="org.detail.main.bottom"
        context={{ orgId: org.id, orgSlug: org.slug, orgName: org.name }}
      />

      {ssoOpen !== null && (
        <SsoConfigDialog
          open={true}
          onOpenChange={(o) => {
            if (!o) setSsoOpen(null);
          }}
          orgId={org.id}
          orgName={org.name}
          orgSlug={org.slug}
          existing={ssoOpen.existing}
          onSaved={async () => {
            await queryClient.invalidateQueries({ queryKey: ['org-sso', org.id] });
          }}
        />
      )}

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        orgId={org.id}
        orgName={org.name}
        onInvited={() => {
          void queryClient.invalidateQueries({ queryKey: ['org-invitations', org.id] });
        }}
      />
    </div>
  );
}

function OrgHeader({ org }: { org: Organisation }): JSX.Element {
  return (
    <header className="flex items-start gap-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Building2 className="h-6 w-6" />
      </div>
      <div className="flex-1">
        <h1 className="text-2xl font-semibold tracking-tight">{org.name}</h1>
        <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
          <span className="font-mono">/{org.slug}</span>
          <span>·</span>
          <span>path {org.path}</span>
          {org.parentOrgId === null && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-primary">
              root
            </span>
          )}
        </div>
      </div>
    </header>
  );
}

function MembersCard({
  members,
  loading,
  onInvite,
}: {
  members: Awaited<ReturnType<typeof api.organisations.members>>;
  loading: boolean;
  onInvite: () => void;
}): JSX.Element {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Members
          </CardTitle>
          <CardDescription>People with a direct membership on this organisation.</CardDescription>
        </div>
        <Button onClick={onInvite}>
          <Plus className="h-4 w-4" />
          Invite member
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : members.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">No members yet.</div>
        ) : (
          <ul className="divide-y divide-border">
            {members.map((m) => (
              <li key={m.membershipId} className="flex items-center justify-between gap-3 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold uppercase text-primary">
                    {initials(m.userName || m.userEmail)}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{m.userName}</div>
                    <div className="font-mono text-xs text-muted-foreground">{m.userEmail}</div>
                  </div>
                </div>
                <PermissionBadges permissions={m.grantedPermissions} />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function InvitationsCard({
  invitations,
  loading,
  onRevoke,
}: {
  invitations: Awaited<ReturnType<typeof api.organisations.pendingInvitations>>;
  loading: boolean;
  onRevoke: (id: string) => void | Promise<void>;
}): JSX.Element {
  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-6 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
        </CardContent>
      </Card>
    );
  }
  if (invitations.length === 0) return <></>;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Pending invitations
        </CardTitle>
        <CardDescription>
          Invitees haven't accepted yet. Revoke if the wrong email was used or it should never have
          been sent.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border">
          {invitations.map((inv) => (
            <li key={inv.id} className="flex items-center justify-between gap-3 py-3">
              <div className="flex-1">
                <div className="text-sm font-medium">{inv.email}</div>
                <div className="text-xs text-muted-foreground">
                  Invited by {inv.invitedByName} · expires{' '}
                  {new Date(inv.expiresAt).toLocaleDateString()}
                </div>
              </div>
              <PermissionBadges permissions={inv.grantedPermissions} />
              <Button variant="ghost" size="sm" onClick={() => onRevoke(inv.id)}>
                <Trash2 className="h-3.5 w-3.5" />
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function SsoCard({
  configs,
  loading,
  onAdd,
  onEdit,
  onDelete,
  onToggleEnabled,
}: {
  configs: OrgSsoConfig[];
  loading: boolean;
  onAdd: () => void;
  onEdit: (cfg: OrgSsoConfig) => void;
  onDelete: (cfg: OrgSsoConfig) => void;
  onToggleEnabled: (cfg: OrgSsoConfig) => void;
}): JSX.Element {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            Single sign-on
          </CardTitle>
          <CardDescription>
            When configured, members of your identity provider (Google Workspace domain, GitHub
            organisation, …) can sign in to this org without an explicit invitation.
          </CardDescription>
        </div>
        <Button onClick={onAdd}>
          <Plus className="h-4 w-4" />
          Configure provider
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : configs.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-background/40 py-6 text-center text-sm text-muted-foreground">
            No SSO providers configured for this org yet.
          </div>
        ) : (
          <ul className="space-y-3">
            {configs.map((cfg) => (
              <SsoConfigRow
                key={cfg.id}
                cfg={cfg}
                onEdit={() => onEdit(cfg)}
                onDelete={() => onDelete(cfg)}
                onToggleEnabled={() => onToggleEnabled(cfg)}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function SsoConfigRow({
  cfg,
  onEdit,
  onDelete,
  onToggleEnabled,
}: {
  cfg: OrgSsoConfig;
  onEdit: () => void;
  onDelete: () => void;
  onToggleEnabled: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const matcherLabel =
    cfg.provider === 'google'
      ? `domain: ${cfg.matcher.workspaceDomain}`
      : `org: ${cfg.matcher.organizationLogin}`;

  async function copy() {
    await navigator.clipboard.writeText(cfg.startUrl);
    setCopied(true);
    toast.success('Start URL copied');
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <li className="space-y-3 rounded-md border border-border bg-background/40 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-primary">
            {cfg.provider}
          </span>
          {!cfg.enabled && (
            <span className="rounded-md border border-border bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              disabled
            </span>
          )}
          {cfg.autoProvision && (
            <span className="rounded-md border border-status-up/40 bg-status-up/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-status-up">
              auto-provision
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onToggleEnabled} title={cfg.enabled ? 'Disable' : 'Enable'}>
            {cfg.enabled ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <KeyValue label="Matcher" value={matcherLabel} />
        <KeyValue label="Client ID" value={`…${cfg.clientIdLast4}`} mono />
        <KeyValue
          label="Default role"
          value={cfg.defaultGrantedPermissions.includes('core.org.admin') ? 'admin' : 'viewer'}
        />
        <KeyValue label="Secret" value={cfg.hasClientSecret ? 'set' : 'missing'} />
      </div>

      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Sign-in URL — share with your team</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded-md border border-border bg-background px-2 py-1.5 font-mono text-xs">
            {cfg.startUrl}
          </code>
          <Button variant="outline" size="sm" onClick={copy}>
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            Copy
          </Button>
        </div>
      </div>
    </li>
  );
}

function KeyValue({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={mono ? 'font-mono text-foreground' : 'text-foreground'}>{value}</span>
    </div>
  );
}

function ActivityCard({ entries, loading }: { entries: AuditEntry[]; loading: boolean }): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-4 w-4" />
          Activity
        </CardTitle>
        <CardDescription>
          Recent actions on this organisation. Every mutating API call is recorded with the actor,
          inputs (secrets redacted), and outcome.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No activity yet. Invites, SSO changes, and other admin actions land here.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {entries.map((e) => (
              <ActivityRow key={e.id} entry={e} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ActivityRow({ entry }: { entry: AuditEntry }): JSX.Element {
  const Icon = entry.outcome === 'success' ? CheckCircle2 : AlertCircle;
  return (
    <li className="flex items-start gap-3 py-3">
      <Icon
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          entry.outcome === 'success' ? 'text-status-up' : 'text-status-down',
        )}
      />
      <div className="flex-1 space-y-0.5">
        <div className="text-sm">
          <span className="font-medium">{entry.actor.label}</span>{' '}
          <span className="text-muted-foreground">{humaniseAction(entry.action)}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span title={new Date(entry.timestamp).toLocaleString()}>{timeAgo(entry.timestamp)}</span>
          <span>·</span>
          <span className="font-mono">via {entry.via}</span>
          <span>·</span>
          <span className="font-mono">{entry.action}</span>
          {entry.outcome !== 'success' && entry.errorMessage && (
            <>
              <span>·</span>
              <span className="text-status-down">{entry.errorMessage}</span>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

/** Map a structured action id to a human verb phrase. Falls back to the raw id. */
function humaniseAction(action: string): string {
  const map: Record<string, string> = {
    'core.org.create': 'created a child organisation',
    'core.invitation.create': 'sent an invitation',
    'core.invitation.revoke': 'revoked an invitation',
    'core.invitation.accept.attach': 'accepted an invitation onto their existing account',
    'core.invitation.accept.create': 'accepted an invitation and created their account',
    'core.sso.create': 'configured an SSO provider',
    'core.sso.update': 'updated an SSO provider',
    'core.sso.delete': 'removed an SSO provider',
  };
  return map[action] ?? `performed ${action}`;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function PermissionBadges({ permissions }: { permissions: string[] }): JSX.Element {
  if (permissions.length === 0) {
    return <span className="text-xs text-muted-foreground">no permissions</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {permissions.map((p) => (
        <span
          key={p}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 font-mono text-[10px] text-muted-foreground"
        >
          {p === 'core.org.admin' && <ShieldCheck className="h-3 w-3 text-primary" />}
          {p}
        </span>
      ))}
    </div>
  );
}

function initials(s: string): string {
  return s
    .split(/[\s@.]+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
